/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */
import {
  getViewFromUrl,
  openIBPopup,
  showError,
  saveMailboxItem,
  closeTaskpane,
  CLOSED_DIALOG_ERROR,
  showSuccess,
  isOutlookForMac,
} from '../helpers'

let currentEvent: Office.AddinCommands.Event | undefined | null = null
let dialog: any

const insertAvailabilityInComposer = (html: string, callback: (result?: any) => void) => {
  Office.context.mailbox.item?.body.setSelectedDataAsync(
    html,
    {
      coercionType: Office.CoercionType.Html,
    },
    (asyncResult: any) => {
      callback(asyncResult)
    }
  )
}

const processEvent = (event: any) => {
  if (event.action === 'meetingDidBook') {
    currentEvent?.completed()
  }
  if (event.action === 'insertAvailability') {
    if (isOutlookForMac()) {
      showSuccess('Availability successfully generated')
    }
    insertAvailabilityInComposer(event.args[1].html, () => {
      currentEvent?.completed()
    })
  }
  if (event.action === 'meetingDidBook' || event.action === 'insertAvailability') {
    dialog?.close()
  }
}

export const getAsyncProspectAndGuests = () => {
  return new Promise<string[]>((resolve, reject) => {
    Office.context.mailbox.item?.to.getAsync(result => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const emails = result.value
        if (!emails.length) {
          reject('Add a main guest first')
        } else {
          Office.context.mailbox.item?.cc.getAsync(ccResult => {
            if (ccResult.status === Office.AsyncResultStatus.Succeeded) {
              const cc = ccResult.value.map(email => email.emailAddress)
              const [prospect, ...guests] = emails.map(email => email.emailAddress)
              const allGuests = cc.concat(guests)
              resolve([prospect, ...allGuests])
            } else {
              reject('Failed getting cc information')
            }
          })
        }
      } else {
        reject('Failed getting prospect information')
      }
    })
  })
}

export const insertAvailability = (event?: Office.AddinCommands.Event) => {
  currentEvent = event
  saveMailboxItem(() => {
    getAsyncProspectAndGuests()
      .then(emails => {
        const [prospect, ...guests] = emails
        openIBPopup(
          {
            prospect: prospect,
            guests: guests.join(','),
            bookWithMe: true,
          },
          { event }
        ).then((asyncValue: any) => {
          if (asyncValue.status === Office.AsyncResultStatus.Succeeded) {
            dialog = asyncValue.value
          }
        })
      })
      .catch(message => {
        showError(message)
        event?.completed()
      })
  })
}

export const bookMeeting = (event?: Office.AddinCommands.Event) => {
  currentEvent = event
  const view = getViewFromUrl()
  if (view === 'compose') {
    saveMailboxItem(() => {
      getAsyncProspectAndGuests()
        .then(emails => {
          const [prospect, ...guests] = emails
          openIBPopup({ prospect: prospect, guests: guests.join(',') }, { event })
        })
        .catch(message => {
          showError(message)
          event?.completed()
        })
    })
  } else {
    const prospect = Office.context.mailbox.item?.from.emailAddress
    const to = Office.context.mailbox.item?.to.map(guest => guest.emailAddress)
    const cc = Office.context.mailbox.item?.cc.map(guest => guest.emailAddress)
    saveMailboxItem(() => {
      openIBPopup({ prospect: prospect, guests: to?.concat(cc || []).join(',') })
    })
  }
}

export const bookMeetingFromOrganizer = (event?: Office.AddinCommands.Event) => {
  currentEvent = event

  const launchIBWithAttendees = (organizerEmail = '', attendees: Office.EmailAddressDetails[]) => {
    const guestsWithoutOrganizer = attendees.filter(guest => {
      return guest.emailAddress !== organizerEmail
    })

    const prospect =
      guestsWithoutOrganizer.find(attendee => attendee.appointmentResponse === 'accepted') ||
      guestsWithoutOrganizer[0]

    const guests = guestsWithoutOrganizer
      .filter(attendee => attendee.emailAddress !== prospect?.emailAddress)
      .map(attendee => attendee.emailAddress)
      .join(',')

    openIBPopup({ prospect: prospect?.emailAddress, guests: guests }, { event }).catch(message => {
      showError(message)
      event?.completed()
    })
  }

  const attendees = Office.context.mailbox.item?.requiredAttendees
  if (!attendees || !Array.isArray(attendees)) {
    Office.context.mailbox.item?.organizer.getAsync(organizerData => {
      Office.context.mailbox.item?.requiredAttendees.getAsync(attendeesData => {
        launchIBWithAttendees(organizerData.value.emailAddress, attendeesData.value)
      })
    })
  } else {
    launchIBWithAttendees(Office.context.mailbox.item?.organizer.emailAddress, attendees)
  }
}

window.addEventListener('message', event => {
  if (event.data.action) {
    processEvent(event.data)
  }
})

/*
  Available global commands
*/

function getGlobal() {
  return typeof self !== 'undefined'
    ? self
    : typeof window !== 'undefined'
    ? window
    : typeof global !== 'undefined'
    ? global
    : undefined
}

const g = getGlobal() as any

export const openReassignPopup = (reportId: string) => {
  /*
    The problem with closeContainer is that the add-in is closed and stopped, so we can't close it while a popup is open
  */
  openIBPopup(
    { reportId },
    {
      dialogEventHandler: event => {
        // Dialog was closed by user
        if (event.error === CLOSED_DIALOG_ERROR) {
          closeTaskpane()
        }
      },
      dialogMessageHandler: event => {
        if (event.action === 'meetingDidBook') {
          closeTaskpane()
        }
      },
    }
  ).then(result => {
    if (result.status === Office.AsyncResultStatus.Succeeded) {
      dialog = result.value
    }
  })
}

g.insertAvailability = (event: Office.AddinCommands.Event) => {
  insertAvailability(event)
}

g.bookMeeting = (event: Office.AddinCommands.Event) => {
  bookMeeting(event)
}

g.bookMeetingFromOrganizer = (event: Office.AddinCommands.Event) => {
  bookMeetingFromOrganizer(event)
}
